import type { Prospect } from "../types";
import { isEmpty, mapRecord, WORKER_URL } from "../prospects/enrichFromSource";
import type { DealLead } from "./types";

/** Prospect fields assembled from a lead (mapRecord output + pipeline meta). */
export type LeadFields = Partial<Prospect> & {
  stage: string;
  source: string;
  source_record_id: string;
};

const AUCTION_META = { stage: "Pre-Auction", source: "DealEngine auction" };
const PROBATE_META = { stage: "Researching", source: "DealEngine probate" };

const phonesFrom = (numbers: string[]) =>
  numbers.filter(Boolean).map((number) => ({
    number: String(number),
    type: "Work" as const,
  }));

/** Minimal mapping from the already-loaded list item (offline fallback). */
export const baseLeadFields = (item: DealLead): LeadFields => {
  const meta = item.kind === "auction" ? AUCTION_META : PROBATE_META;
  const shared = {
    property_address: item.address ?? undefined,
    property_zip: item.zip ?? undefined,
    county: item.county ?? undefined,
    phones: phonesFrom(item.phones ?? []),
    deal_tier: item.dealTier ?? undefined,
    ...meta,
    source_record_id: item.id,
  };
  if (item.kind === "auction") {
    return {
      ...shared,
      owner_name: item.owner ?? undefined,
      beds: item.beds ?? undefined,
      baths: item.baths ?? undefined,
      sqft: item.sqft ?? undefined,
      year_built: item.yearBuilt ?? undefined,
      auction_date: item.saleDate ?? undefined,
      auction_type: item.auctionType ?? undefined,
      min_bid: item.minimumBid ?? undefined,
      opening_bid: item.openingBid ?? undefined,
    };
  }
  return {
    ...shared,
    owner_name: item.decedent ?? undefined,
    avm: item.estValue ?? undefined,
  };
};

/** Fetch the lead's full Airtable record via the Worker; null when unavailable. */
export const fetchAirtableFields = async (
  item: Pick<DealLead, "kind" | "id">,
): Promise<Record<string, any> | null> => {
  const table = item.kind === "auction" ? "Auction Notices" : "Probate Leads";
  const res = await fetch(`${WORKER_URL}/airtable/query`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      table,
      maxRecords: 1,
      filterByFormula: `RECORD_ID()="${item.id}"`,
    }),
  });
  if (!res.ok) throw new Error(`Worker ${res.status}`);
  const data = await res.json();
  return data?.records?.[0]?.fields ?? null;
};

/** Enrich a lead from its full Airtable record (best-effort). */
export const enrichLeadFields = async (item: DealLead): Promise<LeadFields> => {
  try {
    const fields = await fetchAirtableFields(item);
    if (fields) {
      const meta = item.kind === "auction" ? AUCTION_META : PROBATE_META;
      return {
        ...mapRecord(fields, meta.source),
        ...meta,
        source_record_id: item.id,
      };
    }
  } catch {
    // Fall through to the list-item fields below.
  }
  return baseLeadFields(item);
};

const PROSPECT_NUMERIC = new Set([
  "beds",
  "baths",
  "sqft",
  "stories",
  "lot_acres",
  "year_built",
  "tax_value",
  "tax_amount",
  "tax_year",
  "avm",
  "list_price",
  "sold_amount",
  "original_mortgage",
  "estimated_equity",
  "days_on_market",
  "opening_bid",
  "min_bid",
  "lien_amount",
  "lien_position",
  "judgment_amount",
]);

/** Coerce lead fields into an insertable `prospects` row. */
export const fieldsToProspectRow = (
  fields: LeadFields,
  salesId: number | string,
): Record<string, unknown> => {
  const row: Record<string, unknown> = {
    sales_id: salesId,
    stage: fields.stage || "Researching",
  };
  for (const [key, value] of Object.entries(fields)) {
    if (key === "stage" || key === "phones" || key === "emails") continue;
    if (value == null || String(value).trim() === "") continue;
    if (PROSPECT_NUMERIC.has(key)) {
      const n = Number(value);
      if (Number.isFinite(n)) row[key] = n;
    } else {
      row[key] = String(value).trim();
    }
  }
  // owner_name is NOT NULL — default sensibly when the source has no owner.
  if (!row.owner_name) {
    row.owner_name = fields.property_address || "Unknown owner";
  }
  if (Array.isArray(fields.phones) && fields.phones.length) {
    row.phones = fields.phones;
  }
  if (Array.isArray(fields.emails) && fields.emails.length) {
    row.emails = fields.emails;
  }
  return row;
};

/**
 * Fill-empty patch from enriched fields onto an existing prospect: phones are
 * always refreshed (skip-trace results), every other field only fills a
 * currently-empty column so user edits stay intact.
 */
export const fillEmptyPatch = (
  fields: LeadFields,
  existing: Record<string, unknown>,
): Record<string, unknown> => {
  const row = fieldsToProspectRow(fields, existing.sales_id as string);
  delete row.sales_id;
  const patch: Record<string, unknown> = {};
  if (Array.isArray(row.phones) && row.phones.length) {
    patch.phones = row.phones;
  }
  for (const [key, value] of Object.entries(row)) {
    if (key === "phones") continue;
    if (isEmpty(existing[key]) && !isEmpty(value)) patch[key] = value;
  }
  return patch;
};
